import { defineStore } from 'pinia';
import { catalogApi, type Lavador, type Paquete } from '@/lib/api-pos';

const CACHE_TTL_MS = 60_000;

interface CacheEntry<T> {
  data: T;
  loadedAt: number;
  businessDate?: string;
}

function fechaNegocio(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mazatlan' }).format(new Date());
}

export const useCatalogStore = defineStore('catalog', () => {
  const paquetes = new Map<number, CacheEntry<Paquete[]>>();
  const lavadores = new Map<number, CacheEntry<Lavador[]>>();
  const paquetesPendientes = new Map<number, Promise<Paquete[]>>();
  const lavadoresPendientes = new Map<number, Promise<Lavador[]>>();
  let paquetesVersion = 0;
  let lavadoresVersion = 0;

  async function obtenerPaquetes(sucursalId: number): Promise<Paquete[]> {
    const now = Date.now();
    const businessDate = fechaNegocio();
    const cached = paquetes.get(sucursalId);
    if (
      cached &&
      cached.businessDate === businessDate &&
      now - cached.loadedAt < CACHE_TTL_MS
    ) {
      return cached.data;
    }
    const pending = paquetesPendientes.get(sucursalId);
    if (pending) return pending;

    const version = paquetesVersion;
    const request = catalogApi
      .paquetes(sucursalId)
      .then((response) => {
        if (version === paquetesVersion) {
          paquetes.set(sucursalId, {
            data: response.paquetes,
            loadedAt: Date.now(),
            businessDate,
          });
        }
        return response.paquetes;
      })
      .finally(() => {
        if (paquetesPendientes.get(sucursalId) === request) {
          paquetesPendientes.delete(sucursalId);
        }
      });
    paquetesPendientes.set(sucursalId, request);
    return request;
  }

  async function obtenerLavadores(sucursalId: number): Promise<Lavador[]> {
    const now = Date.now();
    const cached = lavadores.get(sucursalId);
    if (cached && now - cached.loadedAt < CACHE_TTL_MS) return cached.data;
    const pending = lavadoresPendientes.get(sucursalId);
    if (pending) return pending;

    const version = lavadoresVersion;
    const request = catalogApi
      .lavadores(sucursalId)
      .then((response) => {
        if (version === lavadoresVersion) {
          lavadores.set(sucursalId, { data: response.lavadores, loadedAt: Date.now() });
        }
        return response.lavadores;
      })
      .finally(() => {
        if (lavadoresPendientes.get(sucursalId) === request) {
          lavadoresPendientes.delete(sucursalId);
        }
      });
    lavadoresPendientes.set(sucursalId, request);
    return request;
  }

  function invalidarPaquetes(sucursalId?: number): void {
    paquetesVersion += 1;
    if (sucursalId === undefined) {
      paquetes.clear();
      paquetesPendientes.clear();
    } else {
      paquetes.delete(sucursalId);
      paquetesPendientes.delete(sucursalId);
    }
  }

  function invalidarLavadores(sucursalId?: number): void {
    lavadoresVersion += 1;
    if (sucursalId === undefined) {
      lavadores.clear();
      lavadoresPendientes.clear();
    } else {
      lavadores.delete(sucursalId);
      lavadoresPendientes.delete(sucursalId);
    }
  }

  function reset(): void {
    invalidarPaquetes();
    invalidarLavadores();
  }

  return {
    obtenerPaquetes,
    obtenerLavadores,
    invalidarPaquetes,
    invalidarLavadores,
    reset,
  };
});
